// Dev panel — visible only when the URL contains ?dev=1.
// Mutates the same Zustand stores the dashboard reads from, so changes apply
// instantly. Phase 5 deletion: `rm -rf src/mock`.

import { useEffect, useMemo, useState } from 'react';
import { useVisitorsStore } from '../stores/visitors';
import { usePresenceStore } from '../stores/presence';
import { useAgentStore } from '../stores/agent';
import { makeFixtureVisitor, seedFixtures } from './fixtures';
import styles from './dev-panel.module.css';
import { getBus, type AgentAvailability, type NetworkConditions } from '@prakash-software/shared';

const AUTO_INTERVAL_MS = 30_000;

export function DevPanel() {
  // Select the raw Map references (stable across renders unless replaced),
  // then derive arrays via useMemo. Returning `Array.from(...)` inline from
  // a Zustand selector creates a new array each call and triggers an
  // infinite render loop.
  const rowsMap = useVisitorsStore((s) => s.rows);
  const othersMap = usePresenceStore((s) => s.others);
  const visitorCount = rowsMap.size;
  const allRows = useMemo(() => Array.from(rowsMap.values()), [rowsMap]);
  const others = useMemo(() => Array.from(othersMap.values()), [othersMap]);

  const me = useAgentStore((s) => s.current);
  const myAvailability = useAgentStore((s) => s.availability);
  const setMyAvailability = useAgentStore((s) => s.setAvailability);
  const upsert = useVisitorsStore((s) => s.upsert);
  const remove = useVisitorsStore((s) => s.remove);
  const setLock = useVisitorsStore((s) => s.setLock);
  const patchOther = usePresenceStore((s) => s.patch);

  const [autoTraffic, setAutoTraffic] = useState(false);
  const [open, setOpen] = useState(true);

  // Network conditions — live state from the bus singleton.
  const bus = useMemo(() => getBus(), []);
  const [conditions, setConditions] = useState<NetworkConditions>(() => bus.getNetworkConditions());
  useEffect(() => {
    return bus.onConditionsChange(setConditions);
  }, [bus]);

  useEffect(() => {
    if (!autoTraffic) return undefined;
    const tick = window.setInterval(() => upsert(makeFixtureVisitor()), AUTO_INTERVAL_MS);
    return () => window.clearInterval(tick);
  }, [autoTraffic, upsert]);

  if (!isDevEnabled()) return null;

  if (!open) {
    return (
      <button type="button" className={styles.tab} onClick={() => setOpen(true)}>
        DEV
      </button>
    );
  }

  return (
    <aside className={styles.panel} aria-label="Developer panel">
      <header className={styles.head}>
        <span className={styles.title}>DEV</span>
        <button type="button" className={styles.close} onClick={() => setOpen(false)}>
          ×
        </button>
      </header>

      <section className={styles.section}>
        <h3 className={styles.h}>Visitors · {visitorCount}</h3>
        <div className={styles.row}>
          <button
            type="button"
            className={styles.btn}
            onClick={() => upsert(makeFixtureVisitor())}
          >
            + Add visitor
          </button>
          <button
            type="button"
            className={styles.btn}
            onClick={() => {
              const last = allRows[allRows.length - 1];
              if (last) remove(last.identity.visitorId);
            }}
          >
            − Remove last
          </button>
        </div>
        <div className={styles.row}>
          <button type="button" className={styles.btn} onClick={() => seedFixtures()}>
            Reseed fixtures
          </button>
          <label className={styles.toggle}>
            <input
              type="checkbox"
              checked={autoTraffic}
              onChange={(e) => setAutoTraffic(e.target.checked)}
            />
            Auto-traffic (30s)
          </label>
        </div>
        <p className={styles.help}>
          Pick any row to soft-lock it from another agent.
        </p>
        <select
          className={styles.select}
          onChange={(e) => {
            const id = e.target.value;
            if (!id) return;
            const other = others[0];
            if (!other) return;
            setLock(id as never, {
              agentId: other.agentId,
              agentName: other.name,
              expiresAt: Date.now() + 30_000,
            });
            e.target.value = '';
          }}
          defaultValue=""
        >
          <option value="" disabled>
            Soft-lock a row…
          </option>
          {allRows.map((r) => (
            <option key={r.identity.visitorId} value={r.identity.visitorId}>
              {r.identity.name ?? r.identity.visitorId.slice(0, 8)} · {r.context.pagePath}
            </option>
          ))}
        </select>
      </section>

      <section className={styles.section}>
        <h3 className={styles.h}>Network · bus</h3>
        <div className={styles.row}>
          <span className={styles.netLabel}>Latency</span>
          <span className={styles.netValue}>
            {conditions.minLatency}–{conditions.maxLatency}ms
          </span>
        </div>
        <input
          type="range"
          min={0}
          max={2000}
          step={50}
          value={conditions.maxLatency}
          onChange={(e) => {
            const max = Number(e.target.value);
            bus.setNetworkConditions({
              maxLatency: max,
              minLatency: Math.min(conditions.minLatency, max),
            });
          }}
          className={styles.range}
          aria-label="Maximum latency"
        />
        <div className={styles.row}>
          <span className={styles.netLabel}>Packet loss</span>
          <span className={styles.netValue}>{Math.round(conditions.packetLoss * 100)}%</span>
        </div>
        <input
          type="range"
          min={0}
          max={100}
          step={5}
          value={Math.round(conditions.packetLoss * 100)}
          onChange={(e) =>
            bus.setNetworkConditions({ packetLoss: Number(e.target.value) / 100 })
          }
          className={styles.range}
          aria-label="Packet loss"
        />
        <button
          type="button"
          className={styles.btn}
          onClick={() => bus.setNetworkConditions({ disconnected: !conditions.disconnected })}
        >
          {conditions.disconnected ? 'Reconnect bus' : 'Disconnect bus'}
        </button>
      </section>

      <section className={styles.section}>
        <h3 className={styles.h}>Other agents</h3>
        {others.map((a) => (
          <div key={a.agentId} className={styles.otherRow}>
            <span className={styles.otherName}>
              {a.initials} · {a.name}
            </span>
            <select
              className={styles.select}
              value={a.availability}
              onChange={(e) =>
                patchOther(a.agentId, { availability: e.target.value as AgentAvailability })
              }
            >
              <option value="available">available</option>
              <option value="away">away</option>
              <option value="offline">offline</option>
            </select>
            <label className={styles.toggle}>
              <input
                type="checkbox"
                checked={a.onVideo}
                onChange={(e) => patchOther(a.agentId, { onVideo: e.target.checked })}
              />
              on video
            </label>
          </div>
        ))}
      </section>

      <section className={styles.section}>
        <h3 className={styles.h}>You · {me?.name ?? '—'}</h3>
        <select
          className={styles.select}
          value={myAvailability}
          onChange={(e) => setMyAvailability(e.target.value as AgentAvailability)}
        >
          <option value="available">available</option>
          <option value="away">away</option>
          <option value="offline">offline</option>
        </select>
      </section>
    </aside>
  );
}

export function isDevEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  return new URLSearchParams(window.location.search).get('dev') === '1';
}
